'use client';
import { Children, isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { useMobileViewport } from '../../lib/useMobile';
import { ActionMenu, type ActionMenuEntry, type ActionMenuItem } from './ActionMenu';
import { Button } from './Button';

/** One bar action as it appears in the folded ⋯ menu: an action row, or the list a choice opens, which
 *  becomes a submenu. A child the bar cannot express that way is pinned in the line instead. */
type FoldedAction =
  | { kind: 'action'; label: string; icon: LucideIcon; tone?: 'danger'; disabled?: boolean; onSelect(): void }
  | { kind: 'submenu'; label: string; iconNode: ReactNode; disabled?: boolean; items: ActionMenuItem[] };

/** The one thing the measurement needs to know about a child, kept in a stable string so the layout effect
 *  that measures can depend on it instead of on a fresh array of elements every render. */
const KEPT = 'kept';
const DANGER_VARIANTS: ReadonlySet<unknown> = new Set(['danger', 'ghost-danger', 'outline-danger']);

const textOf = (value: unknown): string | null =>
  typeof value === 'string' ? value : typeof value === 'number' ? String(value) : null;

/** A rendered component type, not an element: a plain function component or a `forwardRef` one, which is
 *  what every lucide glyph is (`typeof icon` is `'object'` for those, so a `typeof` check alone would take
 *  every real icon for something unrenderable). */
function isComponentType(value: unknown): boolean {
  return typeof value === 'function'
    || (typeof value === 'object' && value !== null && '$$typeof' in (value as Record<string, unknown>));
}

/** A control's accessible name, taken from the same places the shared buttons put one. */
function nameOf(props: Record<string, unknown>): string | null {
  for (const key of ['aria-label', 'label', 'title']) {
    const named = textOf(props[key]);
    if (named !== null) return named;
  }
  return textOf(props.children);
}

/** Read one bar child off the element the caller passed. Every in-tree caller passes a kit control, and
 *  there are two shapes: an action button (`Button` with `icon` and a text child, or `IconButton` with
 *  `icon` and `label`), and a choice that opens a list of its own — the email bundle's move-to-folder
 *  `RowPicker` — whose rows become a submenu of the folded menu. Anything else returns null and stays a
 *  full control in the bar. */
function foldedActionOf(node: ReactNode): FoldedAction | null {
  if (!isValidElement(node)) return null;
  const props = node.props as Record<string, unknown>;
  const label = nameOf(props);
  if (label === null) return null;
  const disabled = props.disabled === true;
  const rows = props.items;
  if (Array.isArray(rows) && typeof props.onChange === 'function') {
    const pick = props.onChange as (value: string) => void;
    const items: ActionMenuItem[] = [];
    for (const row of rows as unknown[]) {
      if (typeof row !== 'object' || row === null) return null;
      const item = row as { id?: unknown; label?: unknown; icon?: unknown; disabled?: unknown };
      const itemLabel = textOf(item.label);
      // Keep the whole picker when any choice cannot become an icon menu item. Never hide only the
      // unrepresentable choices or build a menu the shared vocabulary will refuse on first render.
      if (typeof item.id !== 'string' || itemLabel === null || !item.icon) return null;
      items.push({
        id: item.id,
        label: itemLabel,
        iconNode: item.icon as ReactNode,
        ...(item.disabled === true ? { disabled: true } : {}),
        onSelect: () => pick(item.id as string),
      });
    }
    if (items.length === 0 || !props.icon) return null;
    return { kind: 'submenu', label, iconNode: props.icon as ReactNode, disabled, items };
  }
  const icon = props.icon;
  const onClick = props.onClick;
  if (!isComponentType(icon) || typeof onClick !== 'function') return null;
  return {
    kind: 'action',
    label,
    icon: icon as LucideIcon,
    ...(DANGER_VARIANTS.has(props.variant) ? { tone: 'danger' as const } : {}),
    ...(disabled ? { disabled: true } : {}),
    onSelect: onClick as () => void,
  };
}

/** What a child looks like to the measurement: its wording, its disabled state and whether it is a list.
 *  A change in any of them changes what the child measures, so the row is rendered whole once more. */
const shapeOf = (plan: FoldedAction | null): string => plan === null
  ? KEPT
  : `${plan.label}|${plan.disabled === true ? 'off' : 'on'}|${plan.kind}`;

/** The floating bulk-action bar one register raises over the page while entries are selected. The
 *  memory register owned the only copy; the card registers (skills, agent types, MCP servers) raise
 *  this same bar rather than a lookalike, so there is exactly one implementation of the bottom menu.
 *
 *  `count` is the already-formatted selection line ("Vybráno: 3"), `children` the action buttons and
 *  `onClear` the X button behind `clearLabel`. `disabled` locks the X while the register's bulk action
 *  runs (the action buttons lock themselves); the caller shows the bar while its selection is
 *  non-empty and keeps it a sibling of the layout, never clipped inside it.
 *
 *  ON A PHONE IT IS ONE LINE. A register with eight actions used to wrap the bar onto a second row, over
 *  the very cards the reader had just selected. So the bar measures the room it is allowed — the cap its
 *  own box already carries (`max-w`), less its padding, all read from the rendered row, with no action
 *  count and no per-action constant — keeps the leading actions that fit beside the count and the X, and
 *  folds the rest into a trailing ⋯ `ActionMenu` (the shared kebab) whose rows keep each action's glyph,
 *  wording and danger tone, and whose "choose from a list" children become a submenu. ORDER IS THE
 *  PRIORITY: the caller lists its primary action first and the fold always takes from the end, so no
 *  caller declares a rank and the plugin API grows no field for one. A desktop folds nothing and measures
 *  nothing.
 *
 *  The limit: a child the bar cannot express as a menu row (a nested menu, a node with no label) stays a
 *  full control in the line, because silently dropping an action is worse than a second row. */
export function SelectionActionBar({ count, clearLabel, onClear, disabled = false, children, ref }: {
  ref?: Ref<HTMLDivElement>;
  count: string;
  clearLabel: string;
  onClear: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  const phone = useMobileViewport() === true;
  const own = useRef<HTMLDivElement | null>(null);
  // What each action measured while it was on screen. An action that has folded away keeps its width here,
  // so the next pass can still ask "would it have fitted?" instead of measuring nothing.
  const widths = useRef<Record<number, number>>({});
  const measuredFor = useRef('');
  const [fold, setFold] = useState<{ shapeKey: string; indexes: readonly number[] }>({ shapeKey: '', indexes: [] });
  // A viewport change moves the box's cap without re-rendering anything, so it asks for one more pass.
  const [pass, setPass] = useState(0);
  const actions = Children.toArray(children);
  const plans = actions.map(foldedActionOf);
  const shapes = plans.map(shapeOf);
  const shapeKey = shapes.join('¦');
  // An account or selection change can remove actions before the measuring effect runs. Old indexes
  // belong only to their measured shape; render a changed row whole so it can be measured safely.
  const folded = fold.shapeKey === shapeKey ? fold.indexes : [];
  // What the measuring pass reads. A ref rather than a dependency because `actions`, `plans` and `shapes`
  // are new arrays on every render: a pass that agreed with itself would otherwise be scheduled again for
  // ever. The pass is asked to re-run by what actually changes its answer — the viewport, the children's
  // shape, the current fold — and it then reads the newest row from here.
  const current = useRef({ actions, plans, shapes });
  current.current = { actions, plans, shapes };

  const attach = useCallback((node: HTMLDivElement | null) => {
    own.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as { current: HTMLDivElement | null }).current = node;
  }, [ref]);

  useEffect(() => {
    const onResize = () => setPass((n) => n + 1);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Before the browser paints, so a folded bar never shows up as a wrapped one first. The only inputs are
  // the viewport answer, the children's shape, the current fold and a resize: `folding` below keeps the
  // previous state when the answer has not changed, so a pass that agrees ends the effect rather than
  // scheduling another one.
  useLayoutEffect(() => {
    const folding = (indexes: number[]) => setFold(current =>
      current.shapeKey === shapeKey && indexes.join('¦') === current.indexes.join('¦')
        ? current : { shapeKey, indexes });
    const bar = own.current;
    if (!phone || !bar) {
      folding([]);
      return;
    }
    const { actions: row, shapes: kinds } = current.current;
    if (measuredFor.current !== shapeKey) {
      measuredFor.current = shapeKey;
      widths.current = {};
    }
    for (const element of bar.querySelectorAll<HTMLElement>('[data-bar-action]')) {
      widths.current[Number(element.dataset.barAction)] = element.getBoundingClientRect().width;
    }
    const part = (name: string): number | undefined => {
      const element = bar.querySelector<HTMLElement>(`[data-bar-part="${name}"]`);
      return element === null ? undefined : element.getBoundingClientRect().width;
    };
    const countWidth = part('count');
    const clearWidth = part('clear');
    if (countWidth === undefined || clearWidth === undefined) return;
    // The ⋯ trigger takes the X's step by construction — both are `icon` buttons — so an unrendered menu
    // still has a width to budget with.
    const moreWidth = part('more') ?? clearWidth;
    const style = getComputedStyle(bar);
    const cap = Number.parseFloat(style.maxWidth);
    const gap = Number.parseFloat(style.columnGap);
    const padding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
    if (!Number.isFinite(cap) || !Number.isFinite(gap) || !Number.isFinite(padding)) return;
    // A newly added action has no width yet: render the row whole once so it can be measured.
    if (row.some((_, index) => widths.current[index] === undefined)) {
      folding([]);
      return;
    }
    const budget = cap - padding;
    // Fold from the END until the row fits, which is what makes the caller's order the priority. A child
    // that cannot become a menu row is never folded, so it keeps its place and still costs its width.
    const kept = row.map((_, index) => index);
    const widthOf = () => kept.reduce((sum, index) => sum + gap + (widths.current[index] ?? 0), countWidth)
      + (kept.length < row.length ? gap + moreWidth : 0) + gap + clearWidth;
    for (let index = row.length - 1; index >= 0 && widthOf() > budget; index -= 1) {
      if (kinds[index] === KEPT) continue;
      kept.splice(kept.indexOf(index), 1);
    }
    folding(row.map((_, index) => index).filter((index) => !kept.includes(index)));
  }, [phone, shapeKey, folded, pass]);

  const menu: ActionMenuEntry[] = folded.map((index) => {
    const plan = plans[index];
    if (plan === null || plan === undefined) throw new Error('A folded index must name an expressible action');
    return plan.kind === 'submenu'
      ? { id: `folded-${index}`, label: plan.label, iconNode: plan.iconNode, disabled: plan.disabled, items: plan.items }
      : { id: `folded-${index}`, label: plan.label, icon: plan.icon, tone: plan.tone, disabled: plan.disabled, onSelect: plan.onSelect };
  });

  // `w-max` because a fixed box placed at `left: 50%` shrink-wraps to the half of the window right of its
  // anchor, which folded a bar of seven actions onto two rows on a wide desktop; the cap keeps it inside
  // the window, where it wraps only when the actions really do not fit.
  return (
    <div ref={attach} data-bar className="overlay-layer-fab fixed bottom-6 left-1/2 flex w-max max-w-[calc(100vw-var(--spacing)*8)] -translate-x-1/2 flex-wrap items-center justify-center gap-2 rounded-xl border border-border bg-muted px-3 py-2 shadow-raised animate-fade-up">
      <span data-bar-part="count" className="px-1 text-ui text-foreground">{count}</span>
      {actions.map((action, index) => folded.includes(index) ? null : (
        <span key={`action-${index}`} data-bar-action={index} className="flex shrink-0">{action}</span>
      ))}
      {/* The rest of a crowded phone line, as the shared kebab: its rows keep each action's own glyph,
          wording and danger tone. It names itself from `common.actions`. */}
      {menu.length > 0 ? <span data-bar-part="more" className="flex shrink-0"><ActionMenu variant="kebab" items={menu} /></span> : null}      {/* The X is a control of this row, not a smaller one: it takes the shared icon step, so it stands as
          tall as the default-step actions beside it, on a coarse pointer too. It is measured on itself, so
          the bar keeps its direct children as the row's controls. */}
      <Button data-bar-part="clear" type="button" variant="ghost" size="icon" aria-label={clearLabel} onClick={onClear} disabled={disabled}><X size={15} aria-hidden /></Button>
    </div>
  );
}
